import React, { useState, useContext } from 'react'
import { Image } from '@chakra-ui/react'
import { Icon } from '@chakra-ui/icons'
import { FaShieldHalved } from 'react-icons/fa6'
import { AiFillPushpin, AiOutlinePushpin } from 'react-icons/ai'
import { Flex, Box, Center, IconButton, Tooltip, useDisclosure } from '@chakra-ui/react'

import SidebarItem from './sidebar-item'
import SidebarSettingsMenu from './sidebar-settings-menu'
import NewSidebarContent from './new-sidebar/new-sidebar-content'
import { useSidebarContent } from './tabs'
import { UserContext } from '~config/user-context'
import { useClinic } from '~contexts/clinic-context'
import Logo from '~images/logo/chckvet-icon-bigger.png'
import SharedStack from '~components/shared/wrappers/shared-stack'
import IconWithTooltip from '~components/shared/icons/icon-with-tooltip'
import { UserRoutes } from '~config/routes'
import { Link } from 'gatsby'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import { LocalStorageKeys } from '~config/constants'

const Sidebar = () => {
    // TODO: flip back to `false` once the sidebar help videos ship — the
    // reorganized sidebar becomes the default again at that point. Note this
    // only applies where no stored preference exists; anyone who already
    // toggled keeps their explicit choice.
    const [useLegacySidebar, setUseLegacySidebar] = useLocalStorage<boolean>(
        LocalStorageKeys.useLegacySidebar,
        true,
    )
    const [submenu, setSubmenu] = useState<string>('none')
    const [isHovered, setIsHovered] = useState(false)
    const [isSidebarPinned, setIsSidebarPinned] = useLocalStorage<boolean>(
        LocalStorageKeys.isSidebarPinned,
        false,
    )
    // Controlled here so the sidebar holds itself open while the portaled
    // menu is up. The menu itself closes on outside click / Escape / toggle.
    const settingsMenu = useDisclosure()

    const isExpanded = isSidebarPinned || isHovered || settingsMenu.isOpen

    // Content visibility follows the menu too: an open-but-unhovered sidebar
    // would otherwise go blank (labels are hover-gated) and hide the gear
    // the open menu is anchored to.
    const isContentVisible = isSidebarPinned || settingsMenu.isOpen

    return (
        <Box
            position="relative"
            w={isSidebarPinned ? '228px' : '58px'}
            transition="width 0.2s ease-out"
            display={{ base: 'none', lg: 'initial' }}
            role="group"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Flex
                position="absolute"
                zIndex={9}
                top={0}
                left={0}
                bottom={0}
                p={3}
                gridGap={1}
                direction="column"
                w={isExpanded ? '228px' : '58px'}
                transition="width 0.2s ease-out"
                bg="gray.900"
                shadow="blue.right"
                roundedRight="xl"
                whiteSpace="nowrap"
                overflow={isExpanded ? 'auto' : 'hidden'}
                css={{
                    /* Hide scrollbar for Chrome, Safari and Opera */
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                    /* Hide scrollbar for IE, Edge and Firefox */
                    msOverflowStyle: 'none', // IE and Edge
                    scrollbarWidth: 'none', // Firefox
                }}
            >
                <Flex
                    flexDir="row"
                    w="full"
                    justify="space-between"
                    align="center"
                >
                    <Box as={Link} to={UserRoutes.Home} w="fit-content">
                        <Image
                            src={Logo}
                            alt="Checkvet Logo"
                            borderWidth={1}
                            borderColor="black"
                            h="32px"
                            w="32px"
                            minW="32px"
                            objectFit="contain"
                        />
                    </Box>
                    <Box
                        opacity={isContentVisible ? 1 : 0}
                        display={isContentVisible ? 'block' : 'none'}
                        _groupHover={{
                            display: 'block',
                            opacity: 1,
                        }}
                        transition="all 0.2s"
                    >
                        <Flex align="center" gap={1}>
                            <SidebarPinButton
                                isPinned={isSidebarPinned}
                                onToggle={() =>
                                    setIsSidebarPinned(!isSidebarPinned)
                                }
                            />
                            <SidebarSettingsMenu
                                useLegacySidebar={useLegacySidebar}
                                setUseLegacySidebar={setUseLegacySidebar}
                                menuIsOpen={settingsMenu.isOpen}
                                onMenuOpen={settingsMenu.onOpen}
                                onMenuClose={settingsMenu.onClose}
                            />
                        </Flex>
                    </Box>
                </Flex>
                <SidebarContent
                    isPinned={isContentVisible}
                    submenu={submenu}
                    setSubmenu={setSubmenu}
                    useLegacySidebar={useLegacySidebar}
                />
            </Flex>
        </Box>
    )
}

export default Sidebar

/**
 * Pin toggle next to the settings gear. Pinning keeps the sidebar expanded
 * instead of collapsing back to the icon rail on mouse leave.
 */
const SidebarPinButton: React.FC<{
    isPinned: boolean
    onToggle: () => void
}> = ({ isPinned, onToggle }) => {
    const label = isPinned ? 'Unpin sidebar' : 'Pin sidebar open'

    return (
        <Tooltip label={label} hasArrow>
            <IconButton
                aria-label={label}
                icon={
                    <Icon
                        as={isPinned ? AiFillPushpin : AiOutlinePushpin}
                        fontSize="md"
                    />
                }
                variant="ghost"
                rounded="full"
                size="sm"
                color={isPinned ? 'brand.500' : 'gray.400'}
                bg="transparent"
                transition="all 0.2s"
                _hover={{
                    bg: 'gray.800',
                    color: isPinned ? 'brand.500' : 'white',
                }}
                _active={{ bg: 'gray.800' }}
                onClick={onToggle}
            />
        </Tooltip>
    )
}

export const SidebarContent: React.FC<{
    isPinned?: boolean
    submenu: string
    setSubmenu: (value: React.SetStateAction<string>) => void
    useLegacySidebar: boolean
}> = ({ isPinned = true, submenu, setSubmenu, useLegacySidebar }) => {
    const { clinic, clinicGroup } = useClinic()
    const { isAdmin } = useContext(UserContext)

    const { features } = clinic || {}
    const { sidebarItems, adminItems, enterpriseItems } =
        useSidebarContent(features)

    return (
        <>
            {clinicGroup &&
                enterpriseItems.map(page => (
                    <SidebarItem
                        key={page.label}
                        isPinned={isPinned}
                        {...page}
                        submenu={submenu}
                        setSubmenu={setSubmenu}
                    />
                ))}
            {clinic &&
                (useLegacySidebar ? (
                    sidebarItems.map(page => (
                        <SidebarItem
                            key={page.label}
                            isPinned={isPinned}
                            {...page}
                            submenu={submenu}
                            setSubmenu={setSubmenu}
                        />
                    ))
                ) : (
                    <NewSidebarContent
                        isPinned={isPinned}
                        submenu={submenu}
                        setSubmenu={setSubmenu}
                    />
                ))}
            {isAdmin && (
                <>
                    <SharedStack
                        rounded="lg"
                        spacing={1}
                        borderWidth={1}
                        borderColor="orange.500"
                    >
                        <Center
                            w="full"
                            px="10px"
                            h="24px"
                            justifyContent="flex-start"
                            alignItems="center"
                            bg="orange.500"
                            roundedTop="md"
                        >
                            <IconWithTooltip
                                icon={FaShieldHalved}
                                fontSize="12px"
                                color="white"
                                label="Admin Tools"
                            />
                        </Center>
                        {adminItems.map(page => (
                            <SidebarItem
                                key={page.label}
                                isPinned={isPinned}
                                {...page}
                                submenu={submenu}
                                setSubmenu={setSubmenu}
                            />
                        ))}
                    </SharedStack>
                </>
            )}
        </>
    )
}
